import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const StudentCourses = ({ studentId, studentForm, studentYear }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedNugget, setSelectedNugget] = useState(null);
  const [presentationMode, setPresentationMode] = useState(false);
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({}); // Track submitted answers
  const [visitedSlides, setVisitedSlides] = useState(new Set([0])); // Track visited slides, start with first slide
  const [loading, setLoading] = useState(true);
  
  // Time tracking state
  const [nuggetTimeStarted, setNuggetTimeStarted] = useState(null);
  const [nuggetTimeSpent, setNuggetTimeSpent] = useState(0);
  const [nuggetProgress, setNuggetProgress] = useState({}); // nuggetId -> {timeSpent, lastAccessed, status}

  useEffect(() => {
    if (studentId) {
      loadStudentCourses();
      // loadNuggetProgress() - endpoint not available, progress tracked per-nugget instead
    }
  }, [studentId, studentForm, studentYear]);

  // Load progress for all nuggets in selected topic
  useEffect(() => {
    if (selectedTopic && selectedTopic.nuggets) {
      selectedTopic.nuggets.forEach(nugget => {
        loadNuggetProgressFromBackend(nugget.id);
      });
    }
  }, [selectedTopic]);

  // Timer effect for tracking time in nugget
  useEffect(() => {
    if (presentationMode && nuggetTimeStarted && selectedNugget) {
      const timer = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - nuggetTimeStarted) / 1000);
        setNuggetTimeSpent(elapsedSeconds);
        
        // Save time to backend every 10 seconds
        if (elapsedSeconds > 0 && elapsedSeconds % 10 === 0) {
          saveTimeToBackend(selectedNugget.id, elapsedSeconds);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [presentationMode, nuggetTimeStarted, selectedNugget]);

  // Handle exiting presentation mode - load progress from backend
  useEffect(() => {
    if (!presentationMode && selectedNugget) {
      // Save final time when exiting presentation mode
      if (nuggetTimeSpent > 0) {
        saveTimeToBackend(selectedNugget.id, nuggetTimeSpent);
      }
    }
  }, [presentationMode]);

  // Save time to backend
  const saveTimeToBackend = async (nuggetId, timeSpentSeconds) => {
    try {
      console.log(`📤 Saving time: ${timeSpentSeconds}s for nugget ${nuggetId}`);
      const response = await fetch(
        buildApiUrl(`/api/nuggets/${nuggetId}/update-time?student_email=${encodeURIComponent(studentId)}&time_spent_seconds=${timeSpentSeconds}`),
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Save time failed:', response.status, errorText);
      } else {
        const result = await response.json();
        console.log(`✅ Time saved: ${timeSpentSeconds}s`, result);
      }
    } catch (error) {
      console.error('❌ Failed to save time:', error);
    }
  };

  // Load time from backend
  const loadTimeFromBackend = async (nuggetId) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/nuggets/${nuggetId}/get-time?student_email=${encodeURIComponent(studentId)}`)
      );
      if (response.ok) {
        const data = await response.json();
        const previousTimeSpent = data.time_spent_seconds || 0;
        
        // Set the starting point for this session
        setNuggetTimeStarted(Date.now() - (previousTimeSpent * 1000));
        setNuggetTimeSpent(previousTimeSpent);
        
        console.log(`📊 Loaded previous time: ${previousTimeSpent}s for nugget ${nuggetId}`);
        return previousTimeSpent;
      }
    } catch (error) {
      console.warn('Failed to load time from backend:', error);
    }
    return 0;
  };
  useEffect(() => {
    return () => {
      if (presentationMode && selectedNugget && nuggetTimeStarted) {
        // Reset time tracking
        setNuggetTimeStarted(null);
        
        // Reload progress from backend after session ends
        setTimeout(() => {
          loadNuggetProgressFromBackend(selectedNugget.id);
        }, 500);
      }
    };
  }, [presentationMode, selectedNugget]);

  const loadStudentCourses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ student_id: studentId });
      if (studentForm) params.append('form', studentForm);
      if (studentYear) params.append('year', studentYear);
      
      const response = await fetch(buildApiUrl(`/api/students/${studentId}/courses?${params.toString()}`));
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load nugget progress - endpoint not available in backend
  // Progress is tracked on a per-nugget basis when students submit answers
  /*
  const loadNuggetProgress = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/nuggets/student/${studentId}/progress`));
      if (response.ok) {
        const data = await response.json();
        const progressMap = {};
        data.forEach(item => {
          progressMap[item.nugget_id] = {
            timeSpent: item.time_spent_seconds || 0,
            lastAccessed: item.last_accessed,
            status: item.status || 'not-started',
            currentScore: item.current_score || 0,
            totalQuestions: item.total_questions || 0,
            questionsAnswered: item.questions_answered || 0,
            savedAnswers: item.saved_answers ? JSON.parse(item.saved_answers) : {}
          };
        });
        setNuggetProgress(progressMap);
      }
    } catch (error) {
      console.warn('Failed to load nugget progress:', error);
    }
  };
  */

  // Load nugget progress from backend and update local state
  const loadNuggetProgressFromBackend = async (nuggetId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/nuggets/${nuggetId}/student-progress?student_email=${encodeURIComponent(studentId)}`));
      if (response.ok) {
        const data = await response.json();
        setNuggetProgress(prev => ({
          ...prev,
          [nuggetId]: {
            timeSpent: 0, // Backend doesn't track time currently
            status: data.completion_status === 'completed' ? 'completed' : 'in-progress',
            currentScore: data.total_score || 0,
            totalQuestions: data.total_questions || 0,
            questionsAnswered: data.questions_answered || 0,
            progressPercentage: data.progress_percentage || 0
          }
        }));
      } else {
        console.warn(`Failed to load progress for nugget ${nuggetId}: ${response.status} ${response.statusText}`);
        // Set default progress on failure so UI doesn't break
        setNuggetProgress(prev => ({
          ...prev,
          [nuggetId]: {
            timeSpent: 0,
            status: 'not_started',
            currentScore: 0,
            totalQuestions: 0,
            questionsAnswered: 0,
            progressPercentage: 0
          }
        }));
      }
    } catch (error) {
      console.warn('Failed to load nugget progress from backend:', error);
      // Set default progress on error so UI doesn't break
      setNuggetProgress(prev => ({
        ...prev,
        [nuggetId]: {
          timeSpent: 0,
          status: 'not_started',
          currentScore: 0,
          totalQuestions: 0,
          questionsAnswered: 0,
          progressPercentage: 0
        }
      }));
    }
  };

  const loadSlides = async (nuggetId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/nuggets/${nuggetId}/slides`));
      if (response.ok) {
        const slidesData = await response.json();
        setSlides(slidesData);
        setCurrentSlideIndex(0);
        setPresentationMode(true);
        
        // Load previous time and continue counting
        await loadTimeFromBackend(nuggetId);

        // Load current progress from backend
        await loadNuggetProgressFromBackend(nuggetId);

        // Load saved answers from partial progress if any
        const progress = nuggetProgress[nuggetId];
        if (progress?.savedAnswers) {
          setAnswers(progress.savedAnswers);
          setSubmittedAnswers(progress.savedAnswers);
        }

        // Track student access to the nugget
        try {
          await fetch(buildApiUrl(`/api/nuggets/${nuggetId}/track-access?student_email=${encodeURIComponent(studentId)}&student_name=${encodeURIComponent(studentId)}&student_id=${encodeURIComponent(studentId)}`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
        } catch (trackingError) {
          console.warn('Failed to track nugget access:', trackingError);
        }
      } else {
        // Handle error responses (500, 405, etc.)
        console.error(`Failed to load slides: ${response.status} ${response.statusText}`);
        // Set empty slides array so presentation mode can still start
        setSlides([]);
        setCurrentSlideIndex(0);
        setPresentationMode(true);
        
        // Still load progress even if slides fail
        await loadNuggetProgressFromBackend(nuggetId);
        
        // Show error message to user
        alert(`Failed to load nugget content (${response.status}). The backend may be updating. Please refresh the page.`);
      }
    } catch (error) {
      console.error('Error loading slides:', error);
      // Set empty slides array on network error
      setSlides([]);
      setCurrentSlideIndex(0);
      setPresentationMode(true);
      
      // Still try to load progress
      await loadNuggetProgressFromBackend(nuggetId);
      
      alert('Failed to load nugget content. Please check your connection and try again.');
    }
  };

  const handleAnswer = (elementIndex, answer) => {
    setAnswers({
      ...answers,
      [`${currentSlideIndex}-${elementIndex}`]: answer
    });
  };

  const nextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      const newIndex = currentSlideIndex + 1;
      setCurrentSlideIndex(newIndex);
      setVisitedSlides(prev => new Set([...prev, newIndex]));
    }
  };

  const previousSlide = () => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      setCurrentSlideIndex(newIndex);
      setVisitedSlides(prev => new Set([...prev, newIndex]));
    }
  };

  // Subject icon and color mapping for course cards
  const getSubjectStyle = (subject) => {
    const styles = {
      'English': { icon: '📝', gradient: 'from-blue-500 to-cyan-500', shadow: 'shadow-blue-300/50' },
      'Mathematics': { icon: '🔢', gradient: 'from-green-500 to-emerald-500', shadow: 'shadow-green-300/50' },
      'Arabic': { icon: '🕌', gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-300/50' },
      'Islamic': { icon: '☪️', gradient: 'from-teal-500 to-green-600', shadow: 'shadow-teal-300/50' },
      'Qatar History': { icon: '🏛️', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-300/50' },
      'Science': { icon: '🔬', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-300/50' },
      'History': { icon: '📜', gradient: 'from-yellow-500 to-amber-600', shadow: 'shadow-yellow-300/50' },
    };
    return styles[subject] || { icon: '📖', gradient: 'from-purple-500 to-indigo-600', shadow: 'shadow-purple-300/50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  // Presentation Mode View
  if (presentationMode && slides.length > 0) {
    const currentSlide = slides[currentSlideIndex];
    
    // Hide the parent dashboard header
    document.body.classList.add('presentation-mode');
    
    return (
      <div className="h-screen bg-gray-900 flex fixed inset-0 z-50">
        {/* Left Thumbnail Pane */}
        <div className="w-64 bg-gray-800 border-r border-gray-700 overflow-y-auto flex-shrink-0">
          <div className="p-4 border-b border-gray-700 space-y-3">
            {/* Time Tracker */}
            <div className="bg-green-900 rounded-lg p-3 text-center">
              <div className="text-green-300 text-xs font-semibold uppercase tracking-wide">Time in Nugget</div>
              <div className="text-white text-2xl font-bold font-mono mt-1">
                {Math.floor(nuggetTimeSpent / 60)}m {nuggetTimeSpent % 60}s
              </div>
            </div>
            
            <button
              onClick={async () => {
                // Mark nugget as complete
                if (selectedNugget) {
                  try {
                    // Calculate total score from submitted answers
                    let totalScore = 0;
                    let questionsAnswered = 0;
                    
                    slides.forEach((slide, slideIdx) => {
                      slide.elements?.forEach((element, elemIdx) => {
                        if (element.type === 'question') {
                          const answerKey = `${slideIdx}-${elemIdx}`;
                          if (submittedAnswers[answerKey]) {
                            const userAnswer = answers[answerKey];
                            const isCorrect = userAnswer === element.correctAnswer;
                            questionsAnswered++;
                            if (isCorrect) {
                              totalScore += (100 / (slides.reduce((sum, s) => sum + (s.elements?.filter(e => e.type === 'question').length || 0), 0)));
                            }
                          }
                        }
                      });
                    });
                    
                    // Round score to nearest whole number
                    totalScore = Math.round(totalScore);
                    
                    console.log(`📤 Marking complete: student=${studentId}, score=${totalScore}`);
                    
                    // Mark as complete - use query parameters
                    const completeResponse = await fetch(buildApiUrl(`/api/nuggets/${selectedNugget.id}/mark-complete?student_email=${encodeURIComponent(studentId)}&final_score=${totalScore}`), {
                      method: 'PUT'
                    });
                    
                    if (!completeResponse.ok) {
                      const errorText = await completeResponse.text();
                      console.error('❌ Mark complete failed:', completeResponse.status, errorText);
                    } else {
                      const result = await completeResponse.json();
                      console.log('✅ Nugget marked complete:', result);
                    }
                  } catch (error) {
                    console.warn('Failed to mark nugget complete:', error);
                  }
                }
                
                document.body.classList.remove('presentation-mode');
                setPresentationMode(false);
                setCurrentSlideIndex(0);
                setAnswers({});
                setSubmittedAnswers({});
                setVisitedSlides(new Set([0]));
              }}
              className="w-full flex items-center gap-2 text-white hover:text-gray-300 mb-3 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Exit & Finish
            </button>
            <div className="text-white">
              <h3 className="font-bold text-sm mb-1">{selectedNugget?.name}</h3>
              <p className="text-xs text-gray-400">{slides.length} slides</p>
            </div>
          </div>
          
          {/* Slide Progress Circles */}
          <div className="p-4 space-y-2">
            {slides.map((slide, index) => {
              const slideTitle = slide.elements?.find(el => el.type === 'text')?.title || 
                               slide.elements?.find(el => el.type === 'video')?.title || 
                               slide.elements?.find(el => el.type === 'question')?.text?.substring(0, 40) || 
                               `Slide ${index + 1}`;
              
              const isQuestion = slide.elements?.some(el => el.type === 'question');
              const isVisited = visitedSlides.has(index);
              const isCurrent = index === currentSlideIndex;
              
              return (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlideIndex(index);
                    setVisitedSlides(prev => new Set([...prev, index]));
                  }}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                    isCurrent
                      ? 'bg-green-600/30 hover:bg-green-600/40'
                      : 'hover:bg-gray-700/50'
                  }`}
                >
                  {/* Circle Indicator */}
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isVisited
                      ? 'bg-green-500 border-green-500'
                      : 'bg-gray-700 border-gray-500'
                  }`}>
                    {isQuestion ? (
                      <span className="text-white text-xs font-bold bg-green-600 rounded-full w-6 h-6 flex items-center justify-center">Q</span>
                    ) : (
                      isVisited && <span className="text-white text-lg">✓</span>
                    )}
                  </div>
                  
                  {/* Slide Title */}
                  <div className="flex-1 text-left">
                    <div className={`text-sm font-medium line-clamp-2 ${
                      isCurrent ? 'text-white' : 'text-gray-300'
                    }`}>
                      {slideTitle}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Slide Content */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="flex items-center gap-4 w-full max-w-6xl">
            {/* Left Arrow */}
            <button
              onClick={previousSlide}
              disabled={currentSlideIndex === 0}
              className="p-4 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-8 h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Slide Container */}
            <div className="flex-1 bg-white rounded-xl shadow-2xl p-8 max-h-[600px] overflow-y-auto">
            {currentSlide?.elements && currentSlide.elements.length === 0 ? (
              <div className="text-center py-20">
                <span className="text-6xl">📄</span>
                <p className="text-gray-500 mt-4">This slide is empty</p>
              </div>
            ) : (
              <div className="space-y-6">
                {currentSlide?.elements?.map((element, i) => (
                  <div key={i}>
                    {/* Video Element */}
                    {element.type === 'video' && (
                      <div className="space-y-4">
                        <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                          {(() => {
                            const url = element.url;
                            
                            if (url.includes('youtube.com') || url.includes('youtu.be')) {
                              let videoId = '';
                              if (url.includes('youtube.com/watch?v=')) {
                                videoId = url.split('v=')[1]?.split('&')[0];
                              } else if (url.includes('youtu.be/')) {
                                videoId = url.split('youtu.be/')[1]?.split('?')[0];
                              }
                              
                              return (
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full"
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={element.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              );
                            }
                            
                            if (url.includes('vimeo.com')) {
                              const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                              return (
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full"
                                  src={`https://player.vimeo.com/video/${videoId}`}
                                  title={element.title}
                                  frameBorder="0"
                                  allow="autoplay; fullscreen; picture-in-picture"
                                  allowFullScreen
                                />
                              );
                            }
                            
                            if (url.match(/\.(mp4|webm|ogg)$/i)) {
                              return (
                                <video
                                  className="absolute top-0 left-0 w-full h-full"
                                  controls
                                  src={url}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              );
                            }
                            
                            return (
                              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-100">
                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  Open Video
                                </a>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Image Element */}
                    {element.type === 'image' && (
                      <div className="space-y-4">
                        <h3 className="text-2xl font-bold text-gray-900">{element.title}</h3>
                        {element.url && (
                          <img src={element.url} alt={element.title || 'Image'} className="w-full rounded-lg shadow-lg" />
                        )}
                      </div>
                    )}

                    {/* Text Element */}
                    {element.type === 'text' && (
                      <div className="space-y-4">
                        <h3 className="text-2xl font-bold text-gray-900">{element.title}</h3>
                        <div className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {element.content}
                        </div>
                      </div>
                    )}

                    {/* Question Element */}
                    {element.type === 'question' && (
                      <div className="space-y-4 bg-green-50 p-6 rounded-lg border-2 border-green-200">
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <span>❓</span> {element.text}
                        </h3>
                        
                        {/* Question Image */}
                        {element.questionImage && element.questionImage.trim() !== '' && (
                          <div className="mt-3">
                            <img 
                              src={buildApiUrl(element.questionImage)} 
                              alt="Question" 
                              className="max-h-64 rounded-lg shadow-md"
                            />
                          </div>
                        )}
                        
                        {element.questionType === 'multiple-choice' && element.options && (
                          <div className="space-y-3 mt-4">
                            {element.options.map((option, idx) => {
                              const answerKey = `${currentSlideIndex}-${i}`;
                              const userAnswer = answers[answerKey];
                              const isSelected = userAnswer === option;
                              const isSubmitted = submittedAnswers[answerKey];
                              const isCorrect = option === element.correctAnswer;
                              
                              return (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    if (!isSubmitted) {
                                      handleAnswer(i, option);
                                    }
                                  }}
                                  disabled={isSubmitted}
                                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                    isSubmitted
                                      ? isCorrect
                                        ? 'border-green-400 bg-green-50'
                                        : isSelected
                                        ? 'border-red-400 bg-red-50'
                                        : 'border-gray-200 bg-gray-50'
                                      : isSelected
                                      ? 'border-green-500 bg-green-100'
                                      : 'border-gray-300 hover:border-green-300 bg-white'
                                  } ${isSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                      isSubmitted
                                        ? isCorrect
                                          ? 'border-green-500 bg-green-500 text-white'
                                          : isSelected
                                          ? 'border-red-500 bg-red-500 text-white'
                                          : 'border-gray-400 text-gray-600'
                                        : isSelected
                                        ? 'border-green-500 bg-green-500 text-white'
                                        : 'border-gray-400'
                                    }`}>
                                      {String.fromCharCode(65 + idx)}
                                    </div>
                                    <div className="flex-1">
                                      {element.optionImages && Array.isArray(element.optionImages) && element.optionImages[idx] && element.optionImages[idx].trim() !== '' && (
                                        <img 
                                          src={buildApiUrl(element.optionImages[idx])} 
                                          alt={`Option ${idx + 1}`} 
                                          className="max-h-20 rounded-lg shadow-sm mb-2"
                                        />
                                      )}
                                      <span className="text-gray-900">{option}</span>
                                    </div>
                                    {isSubmitted && isCorrect && (
                                      <span className="text-green-600 font-bold text-xl">✓</span>
                                    )}
                                    {isSubmitted && !isCorrect && isSelected && (
                                      <span className="text-red-600 font-bold text-xl">✗</span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                            
                            {/* Submit Button and Result */}
                            {(() => {
                              const answerKey = `${currentSlideIndex}-${i}`;
                              const userAnswer = answers[answerKey];
                              const isSubmitted = submittedAnswers[answerKey];
                              const isCorrect = userAnswer === element.correctAnswer;
                              
                              const handleSubmitAnswer = async () => {
                                if (userAnswer && selectedNugget) {
                                  try {
                                    const answerPayload = {
                                      nugget_id: selectedNugget.id,
                                      student_email: studentId,
                                      student_name: studentId,
                                      question_id: i,
                                      answer_text: userAnswer,
                                      is_correct: userAnswer === element.correctAnswer
                                    };
                                    console.log('📤 Submitting answer:', answerPayload);
                                    
                                    // Submit answer to backend
                                    const response = await fetch(buildApiUrl(`/api/nuggets/${selectedNugget.id}/submit-answer`), {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify(answerPayload)
                                    });
                                    
                                    if (!response.ok) {
                                      const errorText = await response.text();
                                      console.error('❌ Submit answer failed:', response.status, errorText);
                                    } else {
                                      const result = await response.json();
                                      console.log('✅ Answer submitted:', result);
                                    }
                                    
                                    // Reload progress from backend
                                    await loadNuggetProgressFromBackend(selectedNugget.id);
                                  } catch (error) {
                                    console.warn('Failed to submit answer to backend:', error);
                                  }
                                  
                                  // Update local state
                                  setSubmittedAnswers({
                                    ...submittedAnswers,
                                    [answerKey]: true
                                  });
                                }
                              };
                              
                              return (
                                <div className="mt-4">
                                  {!isSubmitted ? (
                                    <button
                                      onClick={handleSubmitAnswer}
                                      disabled={!userAnswer}
                                      className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-lg"
                                    >
                                      Submit Answer
                                    </button>
                                  ) : (
                                    <div className={`p-4 rounded-lg border-2 ${
                                      isCorrect
                                        ? 'bg-green-50 border-green-400'
                                        : 'bg-red-50 border-red-400'
                                    }`}>
                                      <div className="flex items-center gap-3">
                                        <span className="text-3xl">{isCorrect ? '✅' : '❌'}</span>
                                        <div className="flex-1">
                                          <div className={`font-bold text-lg ${
                                            isCorrect ? 'text-green-700' : 'text-red-700'
                                          }`}>
                                            {isCorrect ? 'Correct! Well done!' : 'Incorrect'}
                                          </div>
                                          {!isCorrect && (
                                            <div className="text-sm text-gray-700 mt-1">
                                              The correct answer is: <span className="font-semibold text-green-700">{element.correctAnswer}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {element.questionType === 'text' && (
                          <textarea
                            value={answers[`${currentSlideIndex}-${i}`] || ''}
                            onChange={(e) => handleAnswer(i, e.target.value)}
                            placeholder="Type your answer here..."
                            className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                            rows={4}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>
            
            {/* Right Arrow */}
            <button
              onClick={nextSlide}
              disabled={currentSlideIndex === slides.length - 1}
              className="p-4 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-8 h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Bottom Progress Indicator */}
        <div className="bg-gray-800 px-6 py-4 flex justify-center">
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlideIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlideIndex ? 'bg-green-500 w-8' : 'bg-gray-600 w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Normal Course View
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back to Dashboard Button */}
        <div className="mb-6">
          <button
            onClick={() => window.location.href = '/student-dashboard'}
            className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg shadow-md transition-all duration-200 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Dashboard</span>
          </button>
        </div>

        {!selectedCourse && (
          <div>
            {courses.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-6xl">📚</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Courses Yet</h3>
                <p className="text-gray-600">Your teacher will assign courses to you soon</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => (
                  <div
                    key={course.id}
                    onClick={() => setSelectedCourse(course)}
                    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-green-400 group"
                  >
                    {(() => { const s = getSubjectStyle(course.subject); return (
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300 mb-4 ring-4 ring-white`}>
                        <span className="text-3xl">{s.icon}</span>
                      </div>
                    ); })()}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">
                        {course.topics?.length || 0} Topics
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedCourse && !selectedTopic && (
          <div>
            <div className="bg-white rounded-xl p-8 shadow-lg mb-6 border-l-4 border-green-500">
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => setSelectedCourse(null)}
                  className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center transition-all shadow-md flex-shrink-0"
                  title="Back to Courses"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-3xl">📖</span>
                <h2 className="text-3xl font-bold text-gray-900">{selectedCourse.name}</h2>
              </div>
              <p className="text-gray-600 ml-12">
                <span className="inline-block bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-medium">
                  {selectedCourse.topics?.length || 0} Topics
                </span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedCourse.topics?.map((topic) => (
                <div
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic)}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-teal-400"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">📑</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{topic.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{topic.description}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full">
                      {topic.nuggets?.length || 0} Lessons
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTopic && (
          <div>
            <div className="bg-white rounded-xl p-8 shadow-lg mb-6 border-l-4 border-teal-500">
              <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{selectedCourse.name}</span>
                <span className="text-gray-400">/</span>
                <span className="font-semibold text-gray-900">{selectedTopic.name}</span>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={() => setSelectedTopic(null)}
                  className="w-10 h-10 bg-teal-500 hover:bg-teal-600 rounded-lg flex items-center justify-center transition-all shadow-md flex-shrink-0"
                  title="Back to Topics"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-3xl">📑</span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedTopic.name}</h2>
                  <p className="text-gray-600 text-sm mt-1">
                    <span className="inline-block bg-teal-100 text-teal-700 px-4 py-1 rounded-full text-xs font-medium">
                      {selectedTopic.nuggets?.length || 0} Lessons
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Lesson</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Progress</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Score</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Time Spent</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedTopic.nuggets?.map((nugget, index) => {
                    const progress = nuggetProgress[nugget.id];
                    const timeSpent = progress?.timeSpent || 0;
                    const timeSpentMinutes = Math.floor(timeSpent / 60);
                    const timeSpentSeconds = timeSpent % 60;
                    const isStarted = progress?.status === 'in-progress' || progress?.status === 'completed';
                    const isCompleted = progress?.status === 'completed';
                    
                    return (
                      <tr
                        key={nugget.id}
                        className="hover:bg-emerald-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-xl">💎</span>
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base font-bold text-gray-900 truncate">{nugget.name}</h3>
                              <p className="text-sm text-gray-600 truncate">{nugget.description}</p>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 text-center">
                          {isCompleted ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              <span>✓</span> Completed
                            </span>
                          ) : isStarted ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                              <span>⏸</span> In Progress
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                              <span>○</span> Not Started
                            </span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4">
                          {progress ? (
                            <div className="space-y-1">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full transition-all"
                                    style={{ width: `${progress.progressPercentage || 0}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-gray-700 min-w-[3rem]">
                                  {progress.progressPercentage || 0}%
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 text-center">
                                {progress.questionsAnswered}/{progress.totalQuestions} questions
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No data</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 text-center">
                          {progress ? (
                            <span className="text-lg font-bold text-green-600">
                              {progress.currentScore.toFixed(0)}
                              <span className="text-sm text-gray-500">/100</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 text-center">
                          {timeSpent > 0 ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              ⏱ {timeSpentMinutes}m {timeSpentSeconds}s
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">0m 0s</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => {
                              setSelectedNugget(nugget);
                              loadSlides(nugget.id);
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all font-medium text-sm shadow-md hover:shadow-lg"
                          >
                            {isStarted ? 'Continue →' : 'Start →'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCourses;
